import React, { memo, useContext, useMemo, useState } from 'react';
import cx from 'classnames';

import { trackEvent } from '@surfline/web-common';
import useCams from 'hooks/useCams';
import { getSpotDetails } from 'selectors/spot';
import { useAppSelector } from 'stores/hooks';
import { SpotDetails } from 'types/spot';
import { RewindsContext } from 'contexts/RewindsContext';
import styles from './PlaybackSpeedControl.module.scss';

type Props = {
  isDisabled: boolean;
  playbackSpeed?: number;
  setPlaybackSpeed: (playbackSpeed: number) => void;
};

const PlaybackSpeedControl = ({ playbackSpeed, setPlaybackSpeed, isDisabled }: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const playbackSpeedWrapperClass = cx({
    'playback-speed-wrapper': true,
    [styles.playbackSpeedWrapper]: true,
    [styles.disabled]: isDisabled,
    [styles.menuOpen]: menuOpen,
  });

  const { isCurrentPlaylistLatest } = useContext(RewindsContext);

  const spot: SpotDetails = useAppSelector(getSpotDetails);
  const { primaryCam } = useCams(spot?.cameras, spot._id);

  const playbackSpeedMap = useMemo(() => {
    const playbackSpeeds = [8, 4, 2, 1.5, 1, 0.5];

    const trackPlaybackSpeedChange = (toSpeed: number) => {
      trackEvent('Playback Speed Changed', {
        camId: primaryCam?._id,
        camName: primaryCam?.alias,
        changedFrom: playbackSpeed?.toString(),
        changedTo: toSpeed?.toString(),
        premiumCam: primaryCam?.isPremium,
        spotId: spot?._id,
        spotName: spot?.name,
        subregionId: spot?.subregion?._id,
        isCurrentClip: isCurrentPlaylistLatest,
      });
    };

    return playbackSpeeds.map((speed, i) => (
      <li key={`${speed}x`}>
        <button
          type="button"
          onClick={() => {
            setPlaybackSpeed(speed);
            trackPlaybackSpeedChange(speed);
            setMenuOpen(false);
          }}
          data-testid={`playback-speed-select-${i}`}
        >{`${speed}x`}</button>
      </li>
    ));
  }, [playbackSpeed, setPlaybackSpeed, primaryCam, spot, isCurrentPlaylistLatest]);

  return (
    <div
      className={playbackSpeedWrapperClass}
      onMouseEnter={() => setMenuOpen(true)}
      onMouseLeave={() => setMenuOpen(false)}
      onFocus={() => setMenuOpen(true)}
      onTouchStart={(e) => {
        e.stopPropagation();
        setMenuOpen(true);
      }}
    >
      <ul className={cx([styles.playbackSpeedSelection, 'playback-speed-selection'])}>
        {playbackSpeedMap}
      </ul>
      <button type="button" className={styles.playbackSpeed} data-testid="playback-speed">
        {playbackSpeed || 1}x
      </button>
    </div>
  );
};

export default memo(PlaybackSpeedControl);
